import { calculatePlanExpiry } from "@memorang/helpers/bundle";
import { isWeb } from "@memorang/helpers/platform";
import type { Plan, ProductType } from "@memorang/types";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "react-native-paper";
import Box from "../Box";
import MarkdownRenderer from "../MarkdownRenderer";
import FeaturesSection from "./FeaturesSection";
import PlansSection, { type PlansSectionProps } from "./PlansSection";
import ProductIconSection, {
	type ProductIconSectionProps,
} from "./ProductIconSection";
import RecommendedPlanText from "./RecommendedPlanText";
import { formatPlanDuration, getSubscriptionIntervalKey } from "./helpers";

export const getAccessDurationTemplate = (
	plan?: Plan,
	expiryDate?: string,
	yearPlan?: Plan,
) => {
	if (!plan) {
		return "";
	}
	const isYearPlan = plan.duration === 12;

	if (isYearPlan) {
		return t("purchase.paywall.accesDuration.yearPlan", {
			duration: formatPlanDuration(plan.interval, plan.duration),
		});
	}

	const expiryText = calculatePlanExpiry(
		plan.interval,
		plan.duration,
		expiryDate,
	);

	return t("purchase.paywall.accesDuration.otherPlan", {
		duration: formatPlanDuration(plan.interval, plan.duration),
		expiryText,
		moreTime: yearPlan
			? t("purchase.paywall.accesDuration.moreTime", {
					duration: formatPlanDuration(yearPlan.interval, yearPlan.duration),
				})
			: "",
	});
};

type Props = {
	features: string[];
	handleRestorePurchase: () => void;
	recommendedPlan?: Plan;
	handleTracking: (eventName: string, params?: Record<string, string>) => void;
	disabled?: boolean;
	loading?: boolean;
	handlePurchase: () => void;
	productType?: ProductType;
	hasAccess?: boolean;
	handleClickAccessHere: () => void;
	purchasable?: boolean;
	fromHomePage?: boolean;
	isSubscriptionEnabled?: boolean;
	appFont?: string;
	disclaimer?: string;
} & PlansSectionProps &
	ProductIconSectionProps;

const IAPModalView = ({
	disabled,
	handlePurchase,
	loading,
	sortedPlans,
	selectedPlan,
	setSelectedPlan,
	expiryDate,
	features,
	url,
	numItems,
	handleTracking,
	productType,
	handleRestorePurchase,
	selectedDate,
	recommendedPlan,
	hasAccess,
	handleClickAccessHere,
	purchasable,
	fromHomePage,
	isSubscriptionEnabled,
	appFont,
	disclaimer,
	subheading,
	title,
	description,
}: Props) => {
	const { t: translate } = useTranslation();

	const price = `$${selectedPlan?.amount}`;

	const getCta = () => {
		if (hasAccess) {
			return translate("purchase.paywall.actions.extendAccess");
		}
		if (isSubscriptionEnabled) {
			return translate("purchase.paywall.actions.subscribe", { price });
		}
		switch (productType) {
			case "STUDY_PACK":
				return translate("purchase.paywall.actions.upgrade");
			default:
				return translate("purchase.paywall.actions.unlock", { price });
		}
	};
	const cta = getCta();

	const UpgradeButton = () => (
		<Button
			mode="contained"
			disabled={disabled}
			uppercase={false}
			onPress={handlePurchase}
			loading={loading}
		>
			{cta}
		</Button>
	);
	const AccessHereButton = () => (
		<Button
			disabled={disabled || loading}
			uppercase={false}
			onPress={handleClickAccessHere}
		>
			{translate("purchase.paywall.actions.accessHere")}
		</Button>
	);

	const restorePurchaseButtonCta = isWeb
		? translate("purchase.paywall.actions.restorePurchaseWeb")
		: translate("purchase.paywall.actions.restorePurchase");
	const RestorePurchaseButton = () => (
		<Button mode="text" onPress={handleRestorePurchase}>
			{restorePurchaseButtonCta}
		</Button>
	);

	const disclaimerText = disclaimer || translate("purchase.paywall.disclaimer");

	const Disclaimer = () => (
		<Box alignItems="center">
			<MarkdownRenderer
				text={disclaimerText}
				variant="explanation"
				fontSize={10}
				useCustomLink={false}
				appFont={appFont}
				customStyles={{
					paragraph: { paddingVertical: 4 },
				}}
			/>
		</Box>
	);

	const accessDurationText = isSubscriptionEnabled
		? translate(
				`purchase.paywall.subscriptionSummary.${getSubscriptionIntervalKey(
					selectedPlan?.interval,
					selectedPlan?.duration,
				)}`,
			)
		: getAccessDurationTemplate(selectedPlan, expiryDate);

	const showAccessDurationText = productType === "STUDY_PACK" && purchasable;
	const showPlansSection =
		productType === "STUDY_PACK" || (productType === "EXAM" && purchasable);
	const showUpgradeButton = purchasable;
	const showFeatures = features.length > 0;
	const showProductIcon = url;
	const showDisclaimer = disclaimerText.length > 0 && purchasable;
	return (
		<Box paddingHorizontal={24} paddingTop={24} flex={1} gap={24}>
			{showProductIcon && (
				<ProductIconSection
					url={url}
					numItems={numItems}
					fromHomePage={fromHomePage}
					selectedDate={selectedDate}
					subheading={subheading}
					title={title}
					description={description}
					appFont={appFont}
				/>
			)}
			{showFeatures && (
				<FeaturesSection features={features} appFont={appFont} />
			)}
			{showPlansSection && (
				<PlansSection
					sortedPlans={sortedPlans}
					selectedPlan={selectedPlan}
					setSelectedPlan={setSelectedPlan}
					expiryDate={expiryDate}
					handleTracking={handleTracking}
					appFont={appFont}
				/>
			)}
			{showAccessDurationText && (
				<MarkdownRenderer
					fontSize={14}
					textAlign="center"
					text={accessDurationText}
				/>
			)}
			<Box gap={8}>
				{showUpgradeButton && <UpgradeButton />}
				{hasAccess && <AccessHereButton />}
			</Box>
			{recommendedPlan && purchasable && showAccessDurationText && (
				<Box
					alignItems="center"
					justifyContent="center"
					maxWidth="90%"
					marginHorizontal="auto"
				>
					<RecommendedPlanText
						recommendedPlan={recommendedPlan}
						selectedDate={selectedDate}
						isSubscriptionEnabled={isSubscriptionEnabled}
					/>
				</Box>
			)}
			<RestorePurchaseButton />
			{showDisclaimer && <Disclaimer />}
		</Box>
	);
};

export default IAPModalView;
